import { useState } from "react";
import { ChevronDown, ChevronUp, Trash2, Tag, CheckCircle } from "lucide-react";
import type { Topic, Sentiment, ResolvedProduct } from "../types";
import { cn } from "../lib/utils";
import { ProductAutocomplete } from "./ProductAutocomplete";
import styles from "./TopicCard.module.css";
import autocompleteStyles from "./ProductAutocomplete.module.css";

interface TopicCardProps {
  topic: Topic;
  index: number;
  onChange: (updated: Topic) => void;
  onRemove: () => void;
}

const SENTIMENT_OPTIONS: { value: Sentiment; label: string; color: string }[] = [
  { value: "positive", label: "Positive", color: "var(--color-success)" },
  { value: "neutral", label: "Neutral", color: "var(--color-text-muted)" },
  { value: "negative", label: "Negative", color: "var(--color-error)" },
  { value: "mixed", label: "Mixed", color: "var(--color-warning)" },
];

/**
 * Editable topic card showing subject, detail, sentiment badge, and product tags.
 * Expands/collapses to keep the form compact.
 *
 * Products can be added via autocomplete (resolved to Sage catalog) or free-text.
 * Resolved products show with a checkmark icon; free-text products show with a tag icon.
 */
export function TopicCard({ topic, index, onChange, onRemove }: TopicCardProps) {
  const [expanded, setExpanded] = useState(false);

  const sentimentOption =
    SENTIMENT_OPTIONS.find((s) => s.value === topic.sentiment) ??
    SENTIMENT_OPTIONS[1];

  const resolvedProducts = topic.resolved_products ?? [];

  function handleFieldChange<K extends keyof Topic>(
    key: K,
    value: Topic[K]
  ) {
    onChange({ ...topic, [key]: value });
  }

  /** Check if a product name has been resolved to a Sage item code. */
  function isResolved(productName: string): ResolvedProduct | undefined {
    return resolvedProducts.find(
      (rp) =>
        rp.display_name === productName ||
        rp.sage_item_code === productName
    );
  }

  /** Add a product selected from the autocomplete dropdown. */
  function handleProductSelect(product: {
    sage_item_code: string;
    display_name: string;
    item_code_desc: string | null;
  }) {
    const displayName = product.display_name || product.sage_item_code;
    // Avoid duplicates — check both display name and sage item code
    if (topic.products_mentioned.includes(displayName)) return;
    if (resolvedProducts.some((rp) => rp.sage_item_code === product.sage_item_code)) return;

    const newResolved: ResolvedProduct = {
      sage_item_code: product.sage_item_code,
      display_name: displayName,
      item_code_desc: product.item_code_desc ?? undefined,
    };

    onChange({
      ...topic,
      products_mentioned: [...topic.products_mentioned, displayName],
      resolved_products: [...resolvedProducts, newResolved],
    });
  }

  /** Add a free-text product (not resolved to catalog). */
  function handleProductFreeText(text: string) {
    const trimmed = text.trim();
    if (!trimmed || topic.products_mentioned.includes(trimmed)) return;
    handleFieldChange("products_mentioned", [
      ...topic.products_mentioned,
      trimmed,
    ]);
  }

  function handleProductRemove(idx: number) {
    const removedName = topic.products_mentioned[idx];
    onChange({
      ...topic,
      products_mentioned: topic.products_mentioned.filter((_, i) => i !== idx),
      resolved_products: resolvedProducts.filter(
        (rp) =>
          rp.display_name !== removedName &&
          rp.sage_item_code !== removedName
      ),
    });
  }

  return (
    <div className={styles.card}>
      {/* Header row — always visible */}
      <button
        type="button"
        className={styles.cardHeader}
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        <span className={styles.topicNumber}>#{index + 1}</span>
        <span className={styles.subjectPreview}>
          {topic.subject || "Untitled topic"}
        </span>
        <span
          className={styles.sentimentBadge}
          style={{ background: sentimentOption.color }}
        >
          {sentimentOption.label}
        </span>
        {expanded ? (
          <ChevronUp className="h-4 w-4 shrink-0 text-text-muted" />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 text-text-muted" />
        )}
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className={styles.cardBody}>
          <label className={styles.fieldLabel}>
            Subject
            <input
              type="text"
              className={styles.textInput}
              value={topic.subject}
              onChange={(e) => handleFieldChange("subject", e.target.value)}
              placeholder="Topic subject"
            />
          </label>

          <label className={styles.fieldLabel}>
            Detail
            <textarea
              className={cn(styles.textInput, styles.textArea)}
              value={topic.detail}
              onChange={(e) => handleFieldChange("detail", e.target.value)}
              placeholder="Discussion details..."
              rows={3}
            />
          </label>

          <label className={styles.fieldLabel}>
            Sentiment
            <div className={styles.segmentedControl}>
              {SENTIMENT_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={cn(
                    styles.segment,
                    topic.sentiment === opt.value && styles.segmentActive
                  )}
                  style={
                    topic.sentiment === opt.value
                      ? { background: opt.color, color: "white" }
                      : undefined
                  }
                  onClick={() => handleFieldChange("sentiment", opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </label>

          {/* Product tags with autocomplete */}
          <div className={styles.fieldLabel}>
            Products Mentioned
            <div className={styles.tags}>
              {topic.products_mentioned.map((p, i) => {
                const resolved = isResolved(p);
                return (
                  <span
                    key={i}
                    className={cn(
                      styles.tag,
                      resolved && autocompleteStyles.resolvedTag
                    )}
                    title={
                      resolved
                        ? `${resolved.sage_item_code} — ${resolved.item_code_desc ?? resolved.display_name}`
                        : undefined
                    }
                  >
                    {resolved ? (
                      <CheckCircle className="h-3 w-3" />
                    ) : (
                      <Tag className="h-3 w-3" />
                    )}
                    {resolved ? resolved.sage_item_code : p}
                    <button
                      type="button"
                      className={styles.tagRemove}
                      onClick={() => handleProductRemove(i)}
                      aria-label={`Remove ${p}`}
                    >
                      &times;
                    </button>
                  </span>
                );
              })}
              <ProductAutocomplete
                onSelectProduct={handleProductSelect}
                onAddFreeText={handleProductFreeText}
                placeholder="Search or type product..."
              />
            </div>
          </div>

          <button
            type="button"
            className={styles.removeButton}
            onClick={onRemove}
          >
            <Trash2 className="h-4 w-4" />
            Remove Topic
          </button>
        </div>
      )}
    </div>
  );
}
